// Behavioral-insights radar chart.
//
// We deliberately render this with hand-rolled SVG instead of pulling
// in Recharts -- the rest of the SPA is dependency-free (the Sidebar
// uses inline icons, the design system uses Tailwind primitives only)
// and a 200-line SVG is cheaper than ~70KB of Recharts for a single
// view.
//
// Visual contract:
//   * One axis per behavioral category (today: Emotional /
//     Psychological + Behavioral; the chart auto-scales if the
//     backend ever surfaces more buckets).
//   * Concentric rings at 1, 2, 3, 4, 5 (the canonical likert scale).
//   * The user's score is plotted as a translucent emerald polygon
//     with a stroked outline; each axis tick is a clickable point
//     that surfaces the contributing question IDs in a tooltip.
//
// A11y: every interactive point is a <button> with an aria-label
// describing the category and score so the chart is navigable by
// keyboard / screen reader.

import { useState } from 'react';

import type { InsightCategory } from '../api/riskApi';

export interface RadarPoint {
    /** Pretty label drawn next to the axis -- e.g. "Behavioral". */
    label: string;
    /** 0-5 score on this axis. */
    score: number;
    /** Question IDs that contributed to this score. */
    questionIds: string[];
    /** "low" | "moderate" | "high" — drives the badge colour. */
    level: string;
    /** Long-form copy from insights.json. */
    description: string;
}

export interface RiskRadarChartProps {
    points: RadarPoint[];
    /** Optional max value -- defaults to 5 (the likert ceiling). */
    max?: number;
    /** Outer SVG size. Defaults to 320px. */
    size?: number;
}

/** Convert a polar (angle, radius) coordinate to cartesian. The angle
 *  origin sits at 12 o'clock and rotates clockwise so axis #0 is at
 *  the top -- the convention every radar chart on the planet uses. */
function polar(cx: number, cy: number, r: number, angleRad: number) {
    return {
        x: cx + r * Math.sin(angleRad),
        y: cy - r * Math.cos(angleRad),
    };
}

const LEVEL_BADGE: Record<string, string> = {
    low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    moderate:
        'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    high: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

export const RiskRadarChart = ({
    points,
    max = 5,
    size = 320,
}: RiskRadarChartProps) => {
    const [activeIdx, setActiveIdx] = useState<number | null>(null);

    if (points.length === 0) {
        return (
            <p className="muted text-sm">No behavioral data to chart yet.</p>
        );
    }

    const cx = size / 2;
    const cy = size / 2;
    // Leave headroom for axis labels around the rim.
    const radius = size / 2 - 32;
    const stepAngle = (2 * Math.PI) / points.length;

    // Geometry helpers --------------------------------------------------------
    const ringRadii = Array.from({ length: max }, (_, i) => ((i + 1) / max) * radius);

    const axisEnds = points.map((_, i) =>
        polar(cx, cy, radius, i * stepAngle),
    );

    const dataPoints = points.map((p, i) => {
        const r = (Math.max(0, Math.min(p.score, max)) / max) * radius;
        return { ...polar(cx, cy, r, i * stepAngle), point: p, index: i };
    });

    const polygonPath = dataPoints
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
        .concat('Z')
        .join(' ');

    const active = activeIdx !== null ? points[activeIdx] : null;

    return (
        <div className="flex flex-col items-center">
            <svg
                viewBox={`0 0 ${size} ${size}`}
                width={size}
                height={size}
                role="img"
                aria-label="Behavioral insights radar chart"
                className="max-w-full"
            >
                {/* Concentric rings. Use stroke-only so the chart blends
                    with whichever surface it's mounted on (light card,
                    dark card, transparent). */}
                {ringRadii.map((r, i) => (
                    <circle
                        key={i}
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill="none"
                        className="stroke-slate-200 dark:stroke-slate-700"
                        strokeWidth={1}
                    />
                ))}

                {/* Axis spokes + labels. */}
                {axisEnds.map((end, i) => {
                    const labelOffset = polar(cx, cy, radius + 18, i * stepAngle);
                    return (
                        <g key={i}>
                            <line
                                x1={cx}
                                y1={cy}
                                x2={end.x}
                                y2={end.y}
                                className="stroke-slate-300 dark:stroke-slate-700"
                                strokeWidth={1}
                            />
                            <text
                                x={labelOffset.x}
                                y={labelOffset.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="fill-slate-600 dark:fill-slate-300 text-[11px] font-semibold"
                            >
                                {points[i].label}
                            </text>
                        </g>
                    );
                })}

                {/* User's score polygon. */}
                <path
                    d={polygonPath}
                    className="fill-emerald-400/30 stroke-emerald-500 dark:fill-emerald-300/20 dark:stroke-emerald-300"
                    strokeWidth={2}
                />

                {/* Interactive vertices -- a <circle> overlaid with a
                    keyboard-focusable <rect> button hitbox for a11y. */}
                {dataPoints.map((dp) => {
                    const isActive = activeIdx === dp.index;
                    return (
                        <g key={dp.index}>
                            <circle
                                cx={dp.x}
                                cy={dp.y}
                                r={isActive ? 7 : 5}
                                className="fill-emerald-500 dark:fill-emerald-300 transition-all"
                            />
                            <circle
                                cx={dp.x}
                                cy={dp.y}
                                r={12}
                                className="fill-transparent cursor-pointer"
                                onMouseEnter={() => setActiveIdx(dp.index)}
                                onMouseLeave={() => setActiveIdx(null)}
                                onFocus={() => setActiveIdx(dp.index)}
                                onBlur={() => setActiveIdx(null)}
                                tabIndex={0}
                                role="button"
                                aria-label={`${dp.point.label}: ${dp.point.score.toFixed(
                                    1,
                                )} of ${max}, ${dp.point.level}`}
                            />
                        </g>
                    );
                })}
            </svg>

            {/* Detail panel — populated when a vertex is hovered or
                focused. Defaults to the highest-scoring category so the
                chart is never empty. */}
            <div className="mt-4 w-full max-w-md surface-card !p-4">
                {active ? (
                    <ActiveDetail point={active} max={max} />
                ) : (
                    <p className="muted text-sm">
                        Hover or focus a point to see which questions contributed.
                    </p>
                )}
            </div>
        </div>
    );
};

const ActiveDetail = ({ point, max }: { point: RadarPoint; max: number }) => {
    const badgeClass =
        LEVEL_BADGE[point.level.toLowerCase()] ??
        'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    return (
        <div>
            <div className="mb-2 flex items-center gap-2">
                <h4 className="section-title">{point.label}</h4>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${badgeClass}`}>
                    {point.level}
                </span>
                <span className="ml-auto text-sm font-bold tabular-nums">
                    {point.score.toFixed(1)} / {max}
                </span>
            </div>
            <p className="text-sm text-slate-700 dark:text-slate-300">
                {point.description}
            </p>
            {point.questionIds.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {point.questionIds.map((qid) => (
                        <span
                            key={qid}
                            className="rounded-md bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-slate-600 dark:text-slate-300"
                        >
                            {qid}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

/** Convenience adapter: turn the backend's ``insights`` object into the
 *  shape the chart expects. Kept in the same module so callers don't
 *  import two files just to render a chart. */
export function insightsToRadarPoints(
    insights: Record<string, InsightCategory>,
): RadarPoint[] {
    // Pretty-print the JSON keys: "emotional_psychological" ->
    // "Emotional / Psychological".
    const PRETTY: Record<string, string> = {
        emotional_psychological: 'Emotional / Psychological',
        behavioral: 'Behavioral',
    };
    return Object.entries(insights).map(([key, cat]) => ({
        label: PRETTY[key] ?? key,
        score: cat.score,
        level: cat.level,
        description: cat.description,
        questionIds: cat.question_ids ?? [],
    }));
}

export default RiskRadarChart;
